import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { api, errMsg, fmtDate, inr } from "@/lib/api";
import { usePageMeta, useSite } from "@/lib/store";
import { WRAP } from "@/components/store/Chrome";
import { Input } from "@/components/ui/input";

function Timeline({ o, stages }) {
  const flow = stages.filter((s) => s.id !== "pending");
  const idx = flow.findIndex((s) => s.id === o.stage_id);
  return (
    <ol className="mt-8 space-y-0" data-testid="track-timeline">
      {flow.map((s, i) => {
        const done = o.payment_status === "paid" && i <= idx;
        return (
          <li key={s.id} className="flex gap-4 items-start">
            <div className="flex flex-col items-center">
              <span className={`w-7 h-7 rounded-full grid place-items-center border-2 ${done ? "bg-red border-[#dc1f26] text-white" : "border-[#ccc] bg-white"}`}>{done && <Check className="w-4 h-4" />}</span>
              {i < flow.length - 1 && <span className={`w-0.5 h-8 ${done && i < idx ? "bg-red" : "bg-[#ddd]"}`} />}
            </div>
            <span className={`pt-1 text-sm ${i === idx && done ? "font-bold" : done ? "" : "text-[#999]"}`}>{s.name}</span>
          </li>
        );
      })}
    </ol>
  );
}

export default function Track() {
  const [params] = useSearchParams();
  const { site } = useSite();
  usePageMeta(site?.pages?.track?.title, site?.pages?.track?.description, site?.pages?.track?.keywords);
  const [number, setNumber] = useState(params.get("number") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");

  const lookup = async (e) => {
    e?.preventDefault();
    setErr("");
    try {
      setO((await api.get("/orders/track", { params: { number, email } })).data);
    } catch (x) {
      setO(null);
      setErr(errMsg(x, "Order not found"));
    }
  };
  useEffect(() => {
    if (params.get("number") && params.get("email")) lookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusLabel = { paid: "Paid", pending: "Awaiting payment", expired: "Payment expired" };
  return (
    <div className={`${WRAP} pt-12 max-w-3xl`}>
      <p className="label-caps text-[11px] text-red mb-1.5">Where's my record?</p>
      <h1 className="font-head font-extrabold text-4xl tracking-tight border-b-2 border-black pb-4">Track your order</h1>
      <form onSubmit={lookup} className="mt-8 grid sm:grid-cols-[1fr_1fr_auto] gap-3" data-testid="track-form">
        <Input placeholder="Order number e.g. VRV100001" value={number} onChange={(e) => setNumber(e.target.value)} className="rounded-none bg-white h-11 font-arial" data-testid="track-number-input" />
        <Input type="email" placeholder="Email used at checkout" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-none bg-white h-11" data-testid="track-email-input" />
        <button className="bg-black text-white px-6 h-11 text-xs font-bold tracking-[0.15em] hover:bg-[#dc1f26]" data-testid="track-submit">TRACK</button>
      </form>
      {err && <p className="mt-6 text-red text-sm" data-testid="track-error">{err}</p>}
      {o && (
        <div className="mt-10 bg-white border border-[#e3e3e3] p-6 sm:p-8" data-testid="track-result">
          <div className="flex flex-wrap justify-between gap-4">
            <div>
              <p className="label-caps text-[10px] text-[#888]">Order</p>
              <p className="font-arial font-bold text-2xl text-red">{o.order_number}</p>
              <p className="text-xs text-[#888] mt-1">Placed {fmtDate(o.created_at)}</p>
            </div>
            <div className="text-right">
              <p className="label-caps text-[10px] text-[#888]">Status</p>
              <p className="font-bold text-lg" data-testid="track-stage">{o.stage_name}</p>
              <p className="text-xs text-[#888]" data-testid="track-payment">{statusLabel[o.payment_status]}</p>
            </div>
          </div>
          {o.payment_status === "paid" && site && <Timeline o={o} stages={site.stages} />}
          {(o.tracking_url || o.tracking_number) && (
            <p className="mt-6 text-sm" data-testid="track-courier">Courier: <b>{[o.courier, o.tracking_number].filter(Boolean).join(" · ")}</b>
              {o.tracking_url && <a href={o.tracking_url} target="_blank" rel="noreferrer" className="ml-2 text-red underline" data-testid="track-courier-link">Track with courier</a>}</p>
          )}
          <ul className="mt-8 border-t border-[#eee] pt-4 space-y-1.5 text-sm">
            {o.items.map((i) => <li key={i.product_id} className="flex justify-between"><span>{i.artist} – {i.title} × {i.qty}{i.status === "not_processed" && <span className="text-red text-xs"> · not processed</span>}{i.status === "refunded" && <span className="text-[#888] text-xs"> · refunded</span>}</span><span className="font-arial">{inr(i.price * i.qty)}</span></li>)}
            <li className="flex justify-between font-bold pt-2"><span>Total</span><span className="font-arial">{inr(o.total)}</span></li>
          </ul>
        </div>
      )}
    </div>
  );
}
